import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  FiServer,
  FiCpu,
  FiHardDrive,
  FiDatabase,
  FiRefreshCw,
  FiShield,
  FiActivity,
  FiClock,
  FiList,
  FiLayers,
  FiAperture,
  FiPieChart,
  FiZap,
  FiBookOpen,
  FiGrid,
  FiCheckCircle,
  FiAlertTriangle,
  FiBell,
  FiCopy,
} from 'react-icons/fi';

const ACCENT = '#22d3ee';
const ACCENT_SOFT = 'rgba(34, 211, 238, 0.14)';
const GLASS = 'rgba(15, 23, 42, 0.55)';
const GLASS_BORDER = 'rgba(148, 163, 184, 0.14)';
const TEXT = '#f8fafc';
const MUTED = '#94a3b8';

const READINESS_NOTIFY_KEY = 'onecMonitorNotifyWhenReady';

function formatIsoRu(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('ru-RU');
}

function playReadyChime() {
  try {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC) return;
    const ctx = new AC();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 880;
    g.gain.value = 0.055;
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    setTimeout(() => {
      try {
        o.stop();
        ctx.close();
      } catch {
        /* noop */
      }
    }, 180);
  } catch {
    /* noop */
  }
}

function TabBtn({ active, onClick, children, icon: Icon }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '10px 16px',
        borderRadius: 999,
        border: 'none',
        cursor: 'pointer',
        fontWeight: 600,
        fontSize: 13,
        letterSpacing: '0.02em',
        transition: 'background 0.2s, color 0.2s, box-shadow 0.2s',
        background: active ? 'linear-gradient(135deg, rgba(34,211,238,0.35) 0%, rgba(56,189,248,0.2) 100%)' : 'transparent',
        color: active ? '#ecfeff' : MUTED,
        boxShadow: active ? '0 0 0 1px rgba(34,211,238,0.35), 0 4px 20px rgba(34,211,238,0.12)' : 'none',
      }}
    >
      {Icon ? <Icon size={16} style={{ opacity: active ? 1 : 0.75 }} /> : null}
      {children}
    </button>
  );
}

function SectionTitle({ icon: Icon, children, hint }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: 12,
            background: ACCENT_SOFT,
            color: ACCENT,
            border: `1px solid ${GLASS_BORDER}`,
          }}
        >
          {Icon ? <Icon size={18} /> : null}
        </span>
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: TEXT, letterSpacing: '-0.02em' }}>{children}</div>
          {hint ? <div style={{ fontSize: 12, color: MUTED, marginTop: 2, maxWidth: 520 }}>{hint}</div> : null}
        </div>
      </div>
    </div>
  );
}

function HeaderCloseButton({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Закрыть"
      style={{
        width: 44,
        height: 44,
        borderRadius: 12,
        border: `1px solid ${GLASS_BORDER}`,
        background: 'rgba(239, 68, 68, 0.12)',
        color: '#fca5a5',
        cursor: 'pointer',
        fontSize: 22,
        fontWeight: 700,
        lineHeight: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        transition: 'background 0.2s, color 0.2s, border-color 0.2s',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.22)';
        e.currentTarget.style.borderColor = 'rgba(248, 113, 113, 0.45)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = 'rgba(239, 68, 68, 0.12)';
        e.currentTarget.style.borderColor = GLASS_BORDER;
      }}
    >
      ×
    </button>
  );
}

function GlassPanel({ children, style = {}, noPadding }) {
  return (
    <div
      style={{
        borderRadius: 16,
        border: `1px solid ${GLASS_BORDER}`,
        background: GLASS,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        boxShadow: '0 8px 32px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.04)',
        padding: noPadding ? 0 : 18,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

/** Полоса 0–100 %: загрузка CPU/RAM/I/O; для заполнения диска используйте warnThreshold. */
function MeterBar({ label, value, hint, warnThreshold = 85, accentBusy = false }) {
  const v = Number.isFinite(value) ? Math.min(100, Math.max(0, value)) : null;
  const hot = v != null && v >= warnThreshold;
  const fill = hot
    ? 'linear-gradient(90deg, #f87171, #fb923c)'
    : accentBusy
      ? 'linear-gradient(90deg, #a78bfa, #22d3ee)'
      : 'linear-gradient(90deg, #34d399, #22d3ee)';
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#e2e8f0' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 800, color: v != null ? TEXT : MUTED }}>{v != null ? `${v}%` : '—'}</span>
      </div>
      <div style={{ height: 10, borderRadius: 8, background: 'rgba(0,0,0,0.35)', overflow: 'hidden' }}>
        <div
          style={{
            width: v != null ? `${v}%` : 0,
            height: '100%',
            borderRadius: 8,
            background: v != null ? fill : 'transparent',
            transition: 'width 0.35s ease',
          }}
        />
      </div>
      {hint ? <div style={{ marginTop: 6, fontSize: 11, color: MUTED, lineHeight: 1.45 }}>{hint}</div> : null}
    </div>
  );
}

/**
 * Мониторинг сервера 1С: данные с API мессенджера; live-события — Socket.IO только пока открыто окно.
 */
export default function ServerOneCMonitorModal({
  open,
  onClose,
  isMobile = false,
  fullViewport = false,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snapshot, setSnapshot] = useState(null);
  const [topFoldersCache, setTopFoldersCache] = useState(null);
  const [topFoldersMessage, setTopFoldersMessage] = useState('');
  const [tab, setTab] = useState('overview');
  const [liveFeed, setLiveFeed] = useState([]);
  const [notifyWhenReady, setNotifyWhenReady] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(READINESS_NOTIFY_KEY) === '1',
  );
  const [readyMomentBanner, setReadyMomentBanner] = useState(null);
  const [copyStaffDone, setCopyStaffDone] = useState(false);
  const readinessLevelPrevRef = useRef(null);

  const requestRemoteFolderScan = useCallback(async (letter) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Нет токена авторизации');
      return false;
    }
    try {
      const r = await fetch('/api/admin/onec-monitor-request-folder-scan', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ letter: String(letter || '').trim() }),
      });
      let j = {};
      try {
        j = await r.json();
      } catch {
        /* noop */
      }
      if (!r.ok) {
        setTopFoldersMessage('');
        setError(typeof j?.error === 'string' ? j.error : `Ошибка ${r.status}`);
        return false;
      }
      setError('');
      const drv = typeof j?.scanDrive === 'string' ? j.scanDrive : String(letter || '').trim();
      setTopFoldersMessage(
        `Запрос «топ папок» для ${drv} поставлен в очередь на сервере 1С. Скан выполнится при следующем запуске задачи onec-monitor-agent.ps1 (обычно до 1–2 мин). Затем обновится список ниже.`,
      );
      return true;
    } catch (e) {
      setTopFoldersMessage('');
      setError(e?.message || 'Сеть недоступна');
      return false;
    }
  }, []);

  const loadSnapshot = useCallback(async (opts) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Нет токена авторизации');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (opts?.largestFolders && /^[A-Za-z]:$/.test(String(opts.largestFolders).trim())) {
        params.set('largestFolders', String(opts.largestFolders).trim());
      }
      const q = params.toString();
      const r = await fetch(`/api/admin/onec-server-monitor${q ? `?${q}` : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (r.status === 404) {
        setSnapshot(null);
        setError('API мониторинга не найден — обновите сервер мессенджера.');
        return;
      }
      if (!r.ok) {
        const t = await r.text();
        let msg = `Ошибка ${r.status}`;
        try {
          const j = JSON.parse(t);
          if (j?.error || j?.message) msg = j.error || j.message;
        } catch {
          if (t && t.length < 200) msg = t;
        }
        setError(msg);
        if (r.status === 401 || r.status === 403 || r.status === 404) {
          setSnapshot(null);
        }
        return;
      }
      const data = await r.json();
      setSnapshot(data && typeof data === 'object' ? data : null);
      const lm = data?.metrics?.largestFolders;
      const ld = data?.metrics?.largestFoldersDrive;
      if (Array.isArray(lm) && lm.length > 0 && ld) {
        setTopFoldersCache({ drive: ld, items: lm });
        setTopFoldersMessage('');
      }
    } catch (e) {
      setError(e?.message || 'Сеть недоступна');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadSnapshot();
  }, [open, loadSnapshot]);

  useEffect(() => {
    if (!open) setTopFoldersMessage('');
  }, [open]);

  useEffect(() => {
    if (!open) {
      setLiveFeed([]);
      return undefined;
    }
    const sock = typeof window !== 'undefined' ? window.socket : null;
    if (!sock || typeof sock.on !== 'function') return undefined;
    const handler = (payload) => {
      try {
        const raw = localStorage.getItem('user');
        const u = raw ? JSON.parse(raw) : {};
        if (String(u?.role || '').toLowerCase() !== 'admin') return;
      } catch {
        return;
      }
      setLiveFeed((prev) => {
        const item = {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          at: payload?.at || new Date().toISOString(),
          kind: payload?.kind || 'alert',
          message: String(payload?.message || ''),
          severity: payload?.severity === 'warn' ? 'warn' : 'info',
          source: 'socket',
        };
        return [item, ...prev].slice(0, 50);
      });
    };
    sock.on('onec-server-monitor-alert', handler);
    return () => {
      try {
        sock.off('onec-server-monitor-alert', handler);
      } catch {
        /* noop */
      }
    };
  }, [open]);

  const { lastRebootMs, lastOperationalMs } = useMemo(() => {
    const rev = Array.isArray(snapshot?.recentEvents) ? snapshot.recentEvents : [];
    let rb = 0;
    let op = 0;
    const bump = (kind, at) => {
      const ms = new Date(at).getTime();
      if (Number.isNaN(ms)) return;
      if (kind === 'windows_reboot' && ms > rb) rb = ms;
      if (kind === 'server_operational' && ms > op) op = ms;
    };
    for (const ev of rev) bump(ev.kind, ev.at);
    for (const ev of liveFeed) bump(ev.kind, ev.at);
    return { lastRebootMs: rb, lastOperationalMs: op };
  }, [snapshot?.recentEvents, liveFeed]);

  /** Жёлтая полоса «ребут зафиксирован, ждём SQL/1С» — чаще опрашиваем API */
  const pollRebootWatch =
    open &&
    snapshot != null &&
    snapshot?.readiness?.level !== 'ok' &&
    lastRebootMs > 0 &&
    lastRebootMs > lastOperationalMs;

  /** Пока открыта модалка — чаще тянем снимок (частота агента на 1С всё равно ограничивает свежесть). При обрыве связи — чаще, чтобы поймать момент подъёма после ребута. */
  useEffect(() => {
    if (!open) return undefined;
    const ms = pollRebootWatch ? 6000 : error ? 5000 : 8000;
    const id = setInterval(() => loadSnapshot(), ms);
    return () => clearInterval(id);
  }, [open, loadSnapshot, pollRebootWatch, error]);

  useEffect(() => {
    if (!open || !pollRebootWatch) return undefined;
    loadSnapshot();
  }, [open, pollRebootWatch, loadSnapshot]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) {
      readinessLevelPrevRef.current = null;
      return;
    }
    const level = snapshot?.readiness?.level;
    if (level === undefined || level === null) return;

    const prev = readinessLevelPrevRef.current;
    const transitionedToOk = prev !== null && prev !== 'ok' && level === 'ok';

    if (transitionedToOk) {
      const host = snapshot?.hostLabel || snapshot?.host || '—';
      const timeRu = formatIsoRu(snapshot?.updatedAt || new Date().toISOString());
      const textForStaff = [
        'Коллеги, сервер 1С снова в работе: SQL Server и службы 1С запущены.',
        'Можно открывать программу 1С и работать.',
        '',
        `Узел: ${host}. Время по данным монитора: ${timeRu}.`,
      ].join('\n');
      setReadyMomentBanner({
        atMs: Date.now(),
        textForStaff,
        timeRu,
        hostLabel: host,
      });
      setCopyStaffDone(false);
      if (notifyWhenReady) {
        playReadyChime();
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          try {
            new Notification('1С: сервер готов к работе', {
              body: `Службы SQL и 1С в норме. Можно сообщить сотрудникам. ${host}`,
              tag: 'onec-server-ready',
              requireInteraction: true,
            });
          } catch {
            /* noop */
          }
        }
      }
    }
    readinessLevelPrevRef.current = level;
  }, [open, snapshot?.readiness?.level, snapshot?.hostLabel, snapshot?.updatedAt, notifyWhenReady]);

  if (!open) return null;

  const desktopOffset = !fullViewport && !isMobile;
  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: desktopOffset ? 320 : 0,
    right: 0,
    bottom: 0,
    width: desktopOffset ? 'calc(100vw - 320px)' : '100vw',
    height: '100vh',
    background: 'rgba(2, 6, 23, 0.72)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'flex-start',
    zIndex: 500001,
    boxSizing: 'border-box',
  };

  const m =
    snapshot?.metrics != null && typeof snapshot.metrics === 'object' && !Array.isArray(snapshot.metrics)
      ? snapshot.metrics
      : snapshot != null && typeof snapshot === 'object'
        ? snapshot
        : {};
  const hostLabel = snapshot?.hostLabel || snapshot?.host || '—';
  const updatedAt = snapshot?.updatedAt || snapshot?.updated_at;
  const folderScanQueued =
    snapshot?.folderScanQueued && typeof snapshot.folderScanQueued === 'object' ? snapshot.folderScanQueued : null;

  const fmtTime = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleString('ru-RU');
  };

  const statCard = (icon, title, value, sub) => (
    <div
      style={{
        padding: '16px 18px',
        borderRadius: 14,
        border: `1px solid ${GLASS_BORDER}`,
        background: 'linear-gradient(165deg, rgba(30,41,59,0.65) 0%, rgba(15,23,42,0.5) 100%)',
        minWidth: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <div style={{ position: 'absolute', top: 0, right: 0, width: 80, height: 80, background: ACCENT_SOFT, borderRadius: '50%', filter: 'blur(28px)', opacity: 0.5, pointerEvents: 'none' }} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, color: ACCENT, position: 'relative' }}>
        {icon}
        <span style={{ fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{title}</span>
      </div>
      <div style={{ fontSize: isMobile ? 22 : 24, fontWeight: 800, color: TEXT, position: 'relative', letterSpacing: '-0.02em' }}>{value}</div>
      {sub ? <div style={{ marginTop: 8, fontSize: 11, color: MUTED, lineHeight: 1.45, position: 'relative' }}>{sub}</div> : null}
    </div>
  );

  const readiness = snapshot?.readiness;
  const recentEvents = Array.isArray(snapshot?.recentEvents) ? snapshot.recentEvents : [];
  const criticalServices = Array.isArray(m?.criticalServices) ? m.criticalServices : [];
  const disks = Array.isArray(m?.disks) ? m.disks : [];
  const logman = m?.logman && typeof m.logman === 'object' ? m.logman : null;
  const largestFolders = Array.isArray(m?.largestFolders) ? m.largestFolders : [];
  const largestFoldersDrive = m?.largestFoldersDrive || null;
  const diskAlert = snapshot?.diskAlert;
  const thC = diskAlert?.thresholdC ?? 80;
  const thO = diskAlert?.thresholdOther ?? 88;
  const cDiskRow = disks.find((d) => String(d.letter || '').toUpperCase().startsWith('C:'));
  const cThresholdLabel =
    cDiskRow && Number.isFinite(cDiskRow.threshold) ? `${cDiskRow.threshold}%` : `${thC}%`;
  const disksSorted = [...disks].sort((a, b) => String(a.letter || '').localeCompare(String(b.letter || ''), 'en'));
  const memoryPctComputed = Number.isFinite(m?.memoryPercent)
    ? m.memoryPercent
    : m?.memoryUsedGb != null && Number.isFinite(m?.memoryTotalGb) && m.memoryTotalGb > 0
      ? Math.round((100 * m.memoryUsedGb) / m.memoryTotalGb)
      : null;

  /** `agentReachable === false` = «снимка нет», не считаем за наличие метрик (иначе красная полоса при пустом ответе). */
  const hasMetrics =
    m &&
    (m.cpuPercent != null ||
      m.memoryPercent != null ||
      m.memoryUsedGb != null ||
      m.diskCPercent != null ||
      m.sqlServerReachable != null ||
      m.oneCClusterReachable != null ||
      m.agentReachable === true ||
      m.lastBootTimeUtc != null ||
      m.uptimeSeconds != null ||
      criticalServices.length > 0 ||
      disks.length > 0 ||
      !!logman);

  const readinessTone =
    readiness?.level === 'ok'
      ? { border: 'rgba(52, 211, 153, 0.45)', bg: 'linear-gradient(135deg, rgba(16,185,129,0.18) 0%, rgba(15,23,42,0.4) 100%)', title: '#6ee7b7' }
      : readiness?.level === 'warn'
        ? { border: 'rgba(251, 191, 36, 0.5)', bg: 'linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(15,23,42,0.45) 100%)', title: '#fcd34d' }
        : readiness?.level === 'partial'
          ? { border: 'rgba(34, 211, 238, 0.4)', bg: 'linear-gradient(135deg, rgba(34,211,238,0.12) 0%, rgba(15,23,42,0.4) 100%)', title: '#67e8f9' }
          : { border: GLASS_BORDER, bg: 'rgba(30, 41, 59, 0.45)', title: '#cbd5e1' };

  const eventKindLabel = (kind) => {
    const map = {
      windows_reboot: 'Перезагрузка Windows',
      sql_up: 'SQL запущен',
      sql_down: 'SQL остановлен',
      onec_up: '1С в работе',
      onec_down: '1С не в норме',
      critical_service_stopped: 'Служба остановлена',
      critical_service_started: 'Служба запущена',
      logman_up: 'Счётчики logman',
      logman_down: 'Счётчики остановлены',
      logman_notfound: 'Logman не найден',
      logman_change: 'Logman',
      disk_over_threshold: 'Диск: порог',
      disk_ok: 'Диск: норма',
      server_operational: 'Можно подключаться к 1С',
      alert: 'Событие',
    };
    return map[kind] || kind || 'Событие';
  };

  const roleLabel = (role) => {
    if (role === 'sql_engine') return 'SQL Server';
    if (role === 'onec_agent') return 'Агент 1С';
    return role || '—';
  };

  const startTypeRu = (t) => {
    const x = String(t || '').toLowerCase();
    if (x === 'auto' || x === 'automatic') return 'Авто';
    if (x === 'manual') return 'Вручную';
    if (x === 'disabled') return 'Отключено';
    if (x === 'boot') return 'Загрузка';
    if (x === 'system') return 'Система';
    return t || '—';
  };

  const tableHead = {
    background: 'rgba(2, 6, 23, 0.55)',
    color: MUTED,
    fontSize: 11,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  };

  const journalMerged = [
    ...liveFeed.map((x) => ({ ...x, sourceLabel: 'Онлайн' })),
    ...recentEvents.slice(0, 25).map((ev, i) => ({
      id: `srv-${ev.at}-${i}`,
      at: ev.at,
      kind: ev.kind,
      message: ev.message,
      severity: 'info',
      source: 'server',
      sourceLabel: 'Сервер',
    })),
  ].sort((a, b) => new Date(b.at) - new Date(a.at));
  const journalDedupe = new Set();
  const journalRows = [];
  for (const row of journalMerged) {
    const key = `${String(row.kind || '')}|${String(row.message || '')}`;
    if (journalDedupe.has(key)) continue;
    journalDedupe.add(key);
    journalRows.push(row);
  }

  const employeesCanConnect = readiness?.level === 'ok';
  const rebootAwaitingServices =
    lastRebootMs > 0 && lastRebootMs > lastOperationalMs;
  const agentAgeSec =
    snapshot?.agentAgeMs != null && Number.isFinite(Number(snapshot.agentAgeMs))
      ? Math.max(0, Math.round(Number(snapshot.agentAgeMs) / 1000))
      : null;

  return (
    <div style={overlayStyle} onClick={onClose} role="presentation">
      <style>{`
        @keyframes onec-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes onec-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        @keyframes onec-ready-glow { 0%, 100% { box-shadow: 0 0 0 1px rgba(52, 211, 153, 0.5), 0 0 28px rgba(16, 185, 129, 0.25); } 50% { box-shadow: 0 0 0 1px rgba(52, 211, 153, 0.85), 0 0 40px rgba(16, 185, 129, 0.45); } }
        .onec-table tbody tr { transition: background 0.15s ease; }
        .onec-table tbody tr:hover { background: rgba(34, 211, 238, 0.06) !important; }
      `}</style>
      <div
        style={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          border: 'none',
          color: TEXT,
          boxSizing: 'border-box',
          background: `
            radial-gradient(ellipse 120% 80% at 10% -20%, rgba(34, 211, 238, 0.12), transparent 50%),
            radial-gradient(ellipse 80% 60% at 100% 0%, rgba(99, 102, 241, 0.1), transparent 45%),
            linear-gradient(180deg, #0f172a 0%, #020617 50%, #0c1222 100%)
          `,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="onec-monitor-title"
      >
        {/* Header */}
        <div
          style={{
            flexShrink: 0,
            padding: isMobile ? '16px 16px 12px' : '20px 24px 16px',
            borderBottom: `1px solid ${GLASS_BORDER}`,
            background: 'rgba(2, 6, 23, 0.4)',
            backdropFilter: 'blur(8px)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 44,
                    height: 44,
                    borderRadius: 14,
                    background: 'linear-gradient(135deg, rgba(34,211,238,0.25) 0%, rgba(99,102,241,0.2) 100%)',
                    border: `1px solid ${GLASS_BORDER}`,
                    color: ACCENT,
                  }}
                >
                  <FiServer size={22} />
                </span>
                <div>
                  <h2 id="onec-monitor-title" style={{ margin: 0, fontSize: isMobile ? '1.2rem' : '1.45rem', fontWeight: 800, letterSpacing: '-0.03em', color: TEXT }}>
                    Мониторинг сервера 1С
                  </h2>
                  <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 13, color: MUTED }}>
                      Узел: <strong style={{ color: '#e2e8f0' }}>{hostLabel}</strong>
                    </span>
                    <span style={{ width: 4, height: 4, borderRadius: '50%', background: GLASS_BORDER }} />
                    <span style={{ fontSize: 12, color: MUTED }}>Обновлено {fmtTime(updatedAt)}</span>
                    {liveFeed.length > 0 ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 4,
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: 'rgba(251, 191, 36, 0.15)',
                          color: '#fcd34d',
                          border: '1px solid rgba(251, 191, 36, 0.35)',
                          animation: 'onec-pulse 2s ease-in-out infinite',
                        }}
                      >
                        <FiZap size={12} /> Live
                      </span>
                    ) : null}
                    {snapshot?.dataSource === 'remote_agent' ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 4,
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: 'rgba(167, 139, 250, 0.18)',
                          color: '#ddd6fe',
                          border: '1px solid rgba(167, 139, 250, 0.35)',
                        }}
                      >
                        Данные с сервера 1С (агент)
                      </span>
                    ) : null}
                    {snapshot?.agentStale ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 4,
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: 'rgba(248, 113, 113, 0.15)',
                          color: '#fecaca',
                          border: '1px solid rgba(248, 113, 113, 0.4)',
                        }}
                        title="Проверьте задачу onec-monitor-agent.ps1 на сервере 1С"
                      >
                        Агент давно не слал снимок{agentAgeSec != null ? ` (${agentAgeSec} с)` : ''}
                      </span>
                    ) : null}
                    {snapshot?.collectCached ? (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 4,
                          padding: '2px 10px',
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 700,
                          background: 'rgba(148, 163, 184, 0.15)',
                          color: '#cbd5e1',
                          border: '1px solid rgba(148, 163, 184, 0.35)',
                        }}
                      >
                        Локальный collect из кэша
                      </span>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0, flexWrap: 'wrap' }}>
              <button
                type="button"
                title="Когда статус сменится на «всё зелёное», короткий звук и уведомление Windows (если разрешено)"
                onClick={async () => {
                  const next = !notifyWhenReady;
                  if (next) {
                    if (typeof Notification !== 'undefined' && Notification.permission === 'default') {
                      try {
                        await Notification.requestPermission();
                      } catch {
                        /* noop */
                      }
                    }
                    localStorage.setItem(READINESS_NOTIFY_KEY, '1');
                    setNotifyWhenReady(true);
                  } else {
                    localStorage.removeItem(READINESS_NOTIFY_KEY);
                    setNotifyWhenReady(false);
                  }
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: `1px solid ${notifyWhenReady ? 'rgba(52, 211, 153, 0.45)' : GLASS_BORDER}`,
                  background: notifyWhenReady ? 'rgba(16, 185, 129, 0.12)' : 'rgba(30, 41, 59, 0.45)',
                  color: notifyWhenReady ? '#6ee7b7' : MUTED,
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: 12,
                }}
              >
                <FiBell size={17} />
                {notifyWhenReady ? 'Сигнал при готовности вкл.' : 'Сигнал при готовности'}
              </button>
              <button
                type="button"
                onClick={() => loadSnapshot()}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '10px 18px',
                  borderRadius: 12,
                  border: `1px solid rgba(34,211,238,0.35)`,
                  background: ACCENT_SOFT,
                  color: ACCENT,
                  cursor: loading ? 'wait' : 'pointer',
                  fontWeight: 700,
                  fontSize: 13,
                }}
              >
                <FiRefreshCw size={17} style={{ animation: loading ? 'onec-spin 0.75s linear infinite' : undefined }} />
                Обновить
              </button>
              <HeaderCloseButton onClick={onClose} />
            </div>
          </div>

          {/* Tabs */}
          <div
            style={{
              marginTop: 16,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              padding: 6,
              borderRadius: 999,
              background: 'rgba(2, 6, 23, 0.5)',
              border: `1px solid ${GLASS_BORDER}`,
              width: 'fit-content',
              maxWidth: '100%',
            }}
          >
            <TabBtn active={tab === 'overview'} onClick={() => setTab('overview')} icon={FiGrid}>
              Обзор
            </TabBtn>
            <TabBtn active={tab === 'infra'} onClick={() => setTab('infra')} icon={FiLayers}>
              Службы и диски
            </TabBtn>
            <TabBtn active={tab === 'journal'} onClick={() => setTab('journal')} icon={FiList}>
              Журнал
              {liveFeed.length ? <span style={{ marginLeft: 4, opacity: 0.9 }}>({liveFeed.length})</span> : null}
            </TabBtn>
            <TabBtn active={tab === 'help'} onClick={() => setTab('help')} icon={FiBookOpen}>
              Справка
            </TabBtn>
          </div>

          {readyMomentBanner && Date.now() - readyMomentBanner.atMs < 15 * 60 * 1000 ? (
            <div
              style={{
                marginTop: 14,
                padding: '14px 16px',
                borderRadius: 16,
                border: '1px solid rgba(52, 211, 153, 0.55)',
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.22) 0%, rgba(15, 23, 42, 0.65) 100%)',
                animation: 'onec-ready-glow 2.2s ease-in-out infinite',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, minWidth: 0 }}>
                  <FiCheckCircle size={26} style={{ color: '#6ee7b7', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 900, fontSize: 16, color: '#ecfdf5', letterSpacing: '-0.02em' }}>
                      Сейчас можно сообщить сотрудникам
                    </div>
                    <div style={{ fontSize: 13, color: '#a7f3d0', lineHeight: 1.55, marginTop: 6 }}>
                      Сервер перешёл в состояние «все службы в норме». Ниже — готовый текст для чата / почты. Время в снимке:{' '}
                      <strong style={{ color: '#ecfdf5' }}>{readyMomentBanner.timeRu}</strong>.
                    </div>
                  </div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard.writeText(readyMomentBanner.textForStaff);
                        setCopyStaffDone(true);
                        setTimeout(() => setCopyStaffDone(false), 2500);
                      } catch {
                        setCopyStaffDone(false);
                      }
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '10px 16px',
                      borderRadius: 12,
                      border: '1px solid rgba(52, 211, 153, 0.45)',
                      background: 'rgba(6, 78, 59, 0.35)',
                      color: '#ecfdf5',
                      fontWeight: 800,
                      fontSize: 13,
                      cursor: 'pointer',
                    }}
                  >
                    <FiCopy size={16} />
                    {copyStaffDone ? 'Скопировано' : 'Копировать текст'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setReadyMomentBanner(null)}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 12,
                      border: `1px solid ${GLASS_BORDER}`,
                      background: 'rgba(15, 23, 42, 0.5)',
                      color: MUTED,
                      fontWeight: 600,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Скрыть
                  </button>
                </div>
              </div>
              <pre
                style={{
                  margin: '12px 0 0',
                  padding: 12,
                  borderRadius: 12,
                  background: 'rgba(2, 6, 23, 0.55)',
                  border: `1px solid ${GLASS_BORDER}`,
                  color: '#e2e8f0',
                  fontSize: 13,
                  lineHeight: 1.55,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontFamily: 'system-ui, sans-serif',
                }}
              >
                {readyMomentBanner.textForStaff}
              </pre>
            </div>
          ) : null}

          {hasMetrics ? (
            <div style={{ marginTop: 14 }}>
              {employeesCanConnect ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 14,
                    border: '1px solid rgba(52, 211, 153, 0.45)',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(15, 23, 42, 0.55) 100%)',
                    boxShadow: '0 0 24px rgba(16, 185, 129, 0.12)',
                  }}
                >
                  <FiCheckCircle size={22} style={{ color: '#6ee7b7', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#ecfdf5', letterSpacing: '-0.02em' }}>
                      Можно работать: сервер готов к 1С
                    </div>
                    <div style={{ fontSize: 13, color: '#a7f3d0', lineHeight: 1.55, marginTop: 4 }}>
                      SQL Server и службы 1С в состоянии «Запущена». Сотрудники могут подключаться к клиенту 1С (как настроено у вас в сети).
                      {lastOperationalMs > lastRebootMs && lastRebootMs > 0 ? (
                        <span style={{ display: 'block', marginTop: 6, fontSize: 12, color: 'rgba(236, 253, 245, 0.85)' }}>
                          После последней перезагрузки Windows готовность снова подтверждена (см. журнал: «Можно подключаться к 1С»).
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : rebootAwaitingServices ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 14,
                    border: '1px solid rgba(251, 191, 36, 0.5)',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.18) 0%, rgba(15, 23, 42, 0.55) 100%)',
                    animation: 'onec-pulse 2.2s ease-in-out infinite',
                  }}
                >
                  <FiAlertTriangle size={22} style={{ color: '#fcd34d', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#fef3c7', letterSpacing: '-0.02em' }}>
                      Перезагрузка Windows зафиксирована
                    </div>
                    <div style={{ fontSize: 13, color: '#fde68a', lineHeight: 1.55, marginTop: 4 }}>
                      Службы SQL или 1С ещё не в норме по снимку. Дождитесь зелёной полосы выше — тогда можно открывать 1С. Снимок в этом режиме опрашивается чаще (~6 с); в обычном режиме — ~8 с. Новые значения CPU/дисков появляются после очередной отправки агента с сервера 1С. Событие «Можно подключаться к 1С» придёт в журнал и по Live, когда оба сервиса снова запущены.
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 14,
                    border: '1px solid rgba(248, 113, 113, 0.35)',
                    background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.12) 0%, rgba(15, 23, 42, 0.5) 100%)',
                  }}
                >
                  <FiAlertTriangle size={22} style={{ color: '#fca5a5', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: 15, color: '#fecaca', letterSpacing: '-0.02em' }}>
                      Подключение к 1С может быть недоступно
                    </div>
                    <div style={{ fontSize: 13, color: '#fecaca', opacity: 0.92, lineHeight: 1.55, marginTop: 4 }}>
                      По текущему снимку не всё в норме. Смотрите блок готовности на «Обзоре» и таблицу служб. Когда статус станет зелёным, можно снова подключать сотрудников.
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </div>

        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: isMobile ? 16 : 24, WebkitOverflowScrolling: 'touch' }}>
          {snapshot?.note && (snapshot?.agentStale || snapshot?.dataSource === 'windows_script_missing') ? (
            <GlassPanel
              style={{
                marginBottom: 20,
                borderColor: snapshot?.agentStale ? 'rgba(248, 113, 113, 0.4)' : 'rgba(251, 191, 36, 0.45)',
                background: snapshot?.agentStale ? 'rgba(127, 29, 29, 0.2)' : 'rgba(120, 53, 15, 0.14)',
              }}
            >
              <div style={{ fontSize: 14, lineHeight: 1.6, color: snapshot?.agentStale ? '#fecaca' : '#fde68a' }}>
                {snapshot.note}
              </div>
            </GlassPanel>
          ) : null}

          {error ? (
            <GlassPanel
              style={{
                marginBottom: 20,
                borderColor: snapshot ? 'rgba(251, 191, 36, 0.45)' : 'rgba(248,113,113,0.35)',
                background: snapshot ? 'rgba(120, 53, 15, 0.14)' : 'rgba(127,29,29,0.2)',
              }}
            >
              <div style={{ color: snapshot ? '#fde68a' : '#fecaca', fontSize: 14, fontWeight: 600 }}>{error}</div>
              {snapshot ? (
                <div style={{ marginTop: 10, fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
                  Опрос не остановлен. Если сейчас перезагружают узел с мессенджером или пропал интернет — это ожидаемо. Ниже остаётся{' '}
                  <strong style={{ color: '#e2e8f0' }}>последний полученный снимок</strong>; после восстановления связи цифры обновятся. Как только снова будет зелёный статус и сработает «Сигнал при готовности», вы поймаете момент, когда можно писать коллегам.
                </div>
              ) : null}
            </GlassPanel>
          ) : null}

          {loading && !hasMetrics && !error ? (
            <GlassPanel style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: MUTED, marginBottom: 14 }}>Загрузка снимка с сервера…</div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 14,
                }}
              >
                {Array.from({ length: 6 }, (_, i) => (
                  <div
                    key={i}
                    style={{
                      height: 108,
                      borderRadius: 14,
                      border: `1px solid ${GLASS_BORDER}`,
                      background: 'rgba(30, 41, 59, 0.45)',
                      animation: 'onec-pulse 1.4s ease-in-out infinite',
                      animationDelay: `${i * 0.08}s`,
                    }}
                  />
                ))}
              </div>
            </GlassPanel>
          ) : null}

          {!hasMetrics && !error && !loading ? (
            <GlassPanel style={{ marginBottom: 20, borderColor: 'rgba(34, 211, 238, 0.25)' }}>
              <div style={{ fontWeight: 800, fontSize: 15, color: '#e2e8f0', marginBottom: 10, letterSpacing: '-0.02em' }}>
                Недостаточно данных
              </div>
              {snapshot?.note ? (
                <div style={{ color: '#e2e8f0', fontSize: 14, lineHeight: 1.65, marginBottom: 12 }}>{snapshot.note}</div>
              ) : null}
              <div style={{ color: MUTED, fontSize: 13, lineHeight: 1.65 }}>
                Обновите снимок. Пока узел не отвечает или метрики не собраны — проверьте агент на сервере 1С (POST{' '}
                <code style={{ color: ACCENT }}>/api/admin/onec-monitor-ingest</code>
                ), секрет <code style={{ color: ACCENT }}>ONEC_MONITOR_INGEST_SECRET</code> на проде и файл{' '}
                <code style={{ color: ACCENT }}>scripts/onec-monitor-collect.ps1</code>, если сбор локальный.
              </div>
              {snapshot?.dataSource && ['windows_script_missing', 'error_fallback', 'local_linux', 'remote_agent', 'local_windows'].includes(snapshot.dataSource) ? (
                <div style={{ marginTop: 12, fontSize: 11, color: MUTED, letterSpacing: '0.04em' }}>
                  Источник:{' '}
                  <span style={{ color: ACCENT }}>
                    {snapshot.dataSource === 'windows_script_missing'
                      ? 'нет collect.ps1 на узле Node — нужен скрипт или агент POST'
                      : snapshot.dataSource === 'error_fallback'
                        ? 'ошибка при формировании снимка (см. текст выше и логи Node)'
                        : snapshot.dataSource === 'local_linux'
                          ? 'узел мессенджера на Linux (без локального сбора Windows)'
                          : snapshot.dataSource === 'remote_agent'
                            ? 'удалённый агент (ingest)'
                            : 'локальный Windows-сбор (collect.ps1)'}
                  </span>
                </div>
              ) : null}
            </GlassPanel>
          ) : null}

          {tab === 'overview' && (
            <>
              {readiness && (
                <GlassPanel style={{ marginBottom: 20, borderColor: readinessTone.border, background: readinessTone.bg }}>
                  <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 10, color: readinessTone.title, letterSpacing: '-0.02em' }}>
                    {readiness.title}
                  </div>
                  <div style={{ fontSize: 14, lineHeight: 1.6, color: '#e2e8f0' }}>{readiness.text}</div>
                </GlassPanel>
              )}

              {diskAlert?.active && Array.isArray(diskAlert.items) && diskAlert.items.length > 0 ? (
                <GlassPanel
                  style={{
                    marginBottom: 20,
                    borderColor: 'rgba(251, 191, 36, 0.5)',
                    background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.14) 0%, rgba(15, 23, 42, 0.55) 100%)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 14 }}>
                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 40,
                        height: 40,
                        borderRadius: 12,
                        background: 'rgba(251, 191, 36, 0.15)',
                        color: '#fcd34d',
                        border: `1px solid rgba(251, 191, 36, 0.35)`,
                        flexShrink: 0,
                      }}
                    >
                      <FiHardDrive size={20} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 17, color: '#fcd34d', letterSpacing: '-0.02em', marginBottom: 6 }}>
                        Диски: заполнение выше порога
                      </div>
                      <div style={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
                        На сервере задано: том <strong style={{ color: '#e2e8f0' }}>C:</strong> — порог{' '}
                        <strong style={{ color: '#e2e8f0' }}>{thC}%</strong>, остальные логические диски —{' '}
                        <strong style={{ color: '#e2e8f0' }}>{thO}%</strong> (переменные окружения{' '}
                        <code style={{ color: ACCENT }}>ONEC_DISK_THRESHOLD_C</code>,{' '}
                        <code style={{ color: ACCENT }}>ONEC_DISK_THRESHOLD_OTHER</code>). При пересечении порога событие попадает в журнал и в сокет для админов.
                      </div>
                    </div>
                  </div>
                  <ul style={{ margin: '0 0 16px', paddingLeft: 20, color: '#e2e8f0', fontSize: 14, lineHeight: 1.75 }}>
                    {diskAlert.items.map((it, i) => (
                      <li key={`${it.letter}-${i}`}>
                        <strong style={{ color: '#fca5a5' }}>{it.letter || '—'}</strong> — занято{' '}
                        {Number.isFinite(it.usedPercent) ? `${it.usedPercent}%` : '—'} при пороге{' '}
                        {Number.isFinite(it.threshold) ? `${it.threshold}%` : '—'}.
                        {Number.isFinite(it.freeGb) ? (
                          <>
                            {' '}
                            Свободно <strong style={{ color: TEXT }}>{it.freeGb} ГБ</strong>
                            {Number.isFinite(it.totalGb) ? <> из {it.totalGb} ГБ</> : null}.
                          </>
                        ) : null}
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => setTab('infra')}
                    style={{
                      padding: '10px 18px',
                      borderRadius: 12,
                      border: `1px solid rgba(34,211,238,0.4)`,
                      background: ACCENT_SOFT,
                      color: ACCENT,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: 13,
                    }}
                  >
                    Таблица дисков и «Топ папок»
                  </button>
                </GlassPanel>
              ) : null}

              {hasMetrics ? (
                <GlassPanel style={{ marginBottom: 20 }}>
                  <SectionTitle
                    icon={FiActivity}
                    hint="Полосы строятся по последнему снимку. Опрос API ~8 с (после ребута ~6 с); сами метрики обновляются, когда агент на 1С снова выполнил collect (задача планировщика — чем чаще, тем «живее» картина)."
                  >
                    Загрузка в снимке
                  </SectionTitle>
                  <MeterBar
                    label="CPU"
                    value={m.cpuPercent}
                    warnThreshold={85}
                    hint="Загрузка процессора (сводный счётчик ОС)"
                  />
                  <MeterBar
                    label="Память ОС"
                    value={memoryPctComputed}
                    warnThreshold={90}
                    hint={
                      m.memoryUsedGb != null && m.memoryTotalGb != null
                        ? `${m.memoryUsedGb} / ${m.memoryTotalGb} ГБ · это ОЗУ Windows, не buffer pool SQL`
                        : 'Доля занятой оперативной памяти'
                    }
                  />
                  {disksSorted.length > 0 ? (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: TEXT, marginBottom: 12, letterSpacing: '-0.02em' }}>Все тома</div>
                      {disksSorted.map((d, idx) => {
                        const thDisk = Number.isFinite(d.threshold) ? d.threshold : String(d.letter || '').toUpperCase().startsWith('C:') ? thC : thO;
                        return (
                          <div
                            key={`ov-disk-${d.letter}-${idx}`}
                            style={{
                              marginBottom: 16,
                              paddingBottom: 16,
                              borderBottom: idx < disksSorted.length - 1 ? `1px solid ${GLASS_BORDER}` : 'none',
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                              <FiHardDrive size={18} style={{ color: d.overThreshold ? '#fca5a5' : ACCENT }} />
                              <span style={{ fontWeight: 800, fontSize: 15, color: d.overThreshold ? '#fca5a5' : TEXT }}>{d.letter || '—'}</span>
                              {d.overThreshold ? (
                                <span style={{ fontSize: 11, fontWeight: 700, color: '#fecaca' }}>выше порога</span>
                              ) : null}
                            </div>
                            <MeterBar
                              label="Заполнение (место)"
                              value={d.usedPercent}
                              warnThreshold={thDisk}
                              hint={
                                Number.isFinite(d.freeGb) && Number.isFinite(d.totalGb)
                                  ? `Свободно ${d.freeGb} ГБ из ${d.totalGb} ГБ · порог для тома ${thDisk}%`
                                  : `Порог для тома ${thDisk}%`
                              }
                            />
                            <MeterBar
                              label="Активность диска (I/O)"
                              value={d.ioBusyPercent}
                              warnThreshold={80}
                              accentBusy
                              hint="Мгновенная занятость тома по I/O (PercentDiskTime); краткие пики обычны"
                            />
                          </div>
                        );
                      })}
                    </div>
                  ) : null}
                </GlassPanel>
              ) : null}

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: 14,
                  marginBottom: 20,
                }}
              >
                {statCard(<FiCpu size={18} />, 'CPU', m.cpuPercent != null ? `${m.cpuPercent}%` : '—', 'Загрузка процессора')}
                {statCard(
                  <FiActivity size={18} />,
                  'Память ОС',
                  m.memoryUsedGb != null && m.memoryTotalGb != null
                    ? `${m.memoryUsedGb} / ${m.memoryTotalGb} ГБ`
                    : m.memoryUsedGb != null
                      ? `${m.memoryUsedGb} ГБ`
                      : '—',
                  'Не buffer pool SQL'
                )}
                {statCard(
                  <FiHardDrive size={18} />,
                  'Диск C:',
                  m.diskCPercent != null ? `${m.diskCPercent}%` : '—',
                  `Системный том · алерт при ≥ ${cThresholdLabel}`
                )}
                {statCard(
                  <FiDatabase size={18} />,
                  'SQL',
                  m.sqlServerReachable === true ? 'Running' : m.sqlServerReachable === false ? 'Стоп' : '—',
                  'Служба Windows'
                )}
                {statCard(
                  <FiLayers size={18} />,
                  '1С',
                  m.oneCClusterReachable === true ? 'Running' : m.oneCClusterReachable === false ? 'Стоп' : '—',
                  'Агент кластера'
                )}
                {statCard(
                  <FiAperture size={18} />,
                  'Снимок',
                  m.agentReachable === true ? 'OK' : m.agentReachable === false ? 'Нет' : '—',
                  'Ответ узла'
                )}
              </div>

              {(m?.lastBootTimeUtc || m?.uptimeHuman) && (
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 14, marginBottom: 8 }}>
                  {statCard(<FiClock size={18} />, 'Загрузка Windows', fmtTime(m.lastBootTimeUtc), 'LastBootUpTime')}
                  {statCard(<FiActivity size={18} />, 'Аптайм', m.uptimeHuman || (m.uptimeSeconds != null ? `${m.uptimeSeconds} с` : '—'), 'С момента загрузки ОС')}
                </div>
              )}

              {disks.length > 0 && diskAlert && !diskAlert.active ? (
                <GlassPanel
                  style={{
                    marginBottom: 12,
                    borderColor: 'rgba(52, 211, 153, 0.28)',
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(15, 23, 42, 0.4) 100%)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: '#a7f3d0', lineHeight: 1.55 }}>
                    <FiHardDrive size={20} style={{ flexShrink: 0, opacity: 0.9 }} />
                    <span>
                      Все <strong style={{ color: '#ecfdf5' }}>{disks.length}</strong> том(а) ниже порога:{' '}
                      <strong style={{ color: '#ecfdf5' }}>C:</strong> до {thC}%, прочие диски до {thO}%. Детали — вкладка «Службы и диски».
                    </span>
                  </div>
                </GlassPanel>
              ) : null}
            </>
          )}

          {tab === 'infra' && (
            <>
              {criticalServices.length > 0 && (
                <GlassPanel style={{ marginBottom: 20 }}>
                  <SectionTitle icon={FiShield} hint="После перезагрузки проверяйте статус и автозапуск (Авто / Вручную).">
                    Критичные службы
                  </SectionTitle>
                  <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${GLASS_BORDER}` }}>
                    <table className="onec-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={tableHead}>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Роль</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Имя</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Отображаемое имя</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Статус</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Старт</th>
                        </tr>
                      </thead>
                      <tbody>
                        {criticalServices.map((row, idx) => {
                          const ok = row.ok === true;
                          return (
                            <tr
                              key={`${row.serviceName}-${idx}`}
                              style={{
                                borderTop: `1px solid ${GLASS_BORDER}`,
                                background: ok ? 'rgba(16, 185, 129, 0.06)' : 'rgba(239, 68, 68, 0.1)',
                              }}
                            >
                              <td style={{ padding: '12px 14px', color: '#e2e8f0' }}>{roleLabel(row.role)}</td>
                              <td style={{ padding: '12px 14px', fontWeight: 700, color: TEXT }}>{row.serviceName || '—'}</td>
                              <td style={{ padding: '12px 14px', color: MUTED, maxWidth: 280 }}>{row.displayName || '—'}</td>
                              <td style={{ padding: '12px 14px', fontWeight: 800, color: ok ? '#6ee7b7' : '#fca5a5' }}>{row.status || '—'}</td>
                              <td style={{ padding: '12px 14px', color: MUTED }}>{startTypeRu(row.startType)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </GlassPanel>
              )}

              {disks.length > 0 && (
                <GlassPanel style={{ marginBottom: 20 }}>
                  <SectionTitle
                    icon={FiHardDrive}
                    hint={`Пороги с сервера: C: — ${thC}%, остальные тома — ${thO}%. Строка подсвечивается, если заполнение ≥ порога для этого тома. Журнал и сокет получают событие при переходе через порог.`}
                  >
                    Диски
                  </SectionTitle>
                  {snapshot?.dataSource === 'remote_agent' ? (
                    <p style={{ margin: '0 0 14px', fontSize: 12, color: MUTED, lineHeight: 1.55 }}>
                      Удалённый сервер 1С: кнопка «Топ папок» ставит задание в очередь; скан выполняется при{' '}
                      <strong style={{ color: '#e2e8f0' }}>следующем</strong> запуске{' '}
                      <code style={{ color: ACCENT }}>onec-monitor-agent.ps1</code> на этом сервере (обновите скрипт агента на 1С).
                    </p>
                  ) : null}
                  {folderScanQueued?.scanDrive ? (
                    <p style={{ margin: '0 0 14px', fontSize: 13, color: '#fde68a', lineHeight: 1.5 }}>
                      В очереди скан тома <strong style={{ color: '#fef3c7' }}>{folderScanQueued.scanDrive}</strong> — ждите
                      прогона агента.
                    </p>
                  ) : null}
                  {topFoldersMessage ? (
                    <p style={{ margin: '0 0 14px', fontSize: 13, color: '#a7f3d0', lineHeight: 1.55 }}>{topFoldersMessage}</p>
                  ) : null}
                  <div style={{ overflowX: 'auto', borderRadius: 12, border: `1px solid ${GLASS_BORDER}` }}>
                    <table className="onec-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={tableHead}>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Том</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Заполнение</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Свободно</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Всего</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }}>Порог</th>
                          <th style={{ padding: '12px 14px', textAlign: 'left' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {disks.map((d, idx) => {
                          const bad = !!d.overThreshold;
                          const pct = Number.isFinite(d.usedPercent) ? d.usedPercent : 0;
                          return (
                            <tr
                              key={`${d.letter}-${idx}`}
                              style={{
                                borderTop: `1px solid ${GLASS_BORDER}`,
                                background: bad ? 'rgba(239, 68, 68, 0.08)' : 'rgba(16, 185, 129, 0.04)',
                              }}
                            >
                              <td style={{ padding: '12px 14px', fontWeight: 800, color: bad ? '#fca5a5' : '#6ee7b7' }}>{d.letter || '—'}</td>
                              <td style={{ padding: '12px 14px', minWidth: 140 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                  <div style={{ flex: 1, height: 8, borderRadius: 6, background: 'rgba(0,0,0,0.35)', overflow: 'hidden' }}>
                                    <div
                                      style={{
                                        width: `${Math.min(100, pct)}%`,
                                        height: '100%',
                                        borderRadius: 6,
                                        background: bad ? 'linear-gradient(90deg, #f87171, #fb923c)' : 'linear-gradient(90deg, #34d399, #22d3ee)',
                                        transition: 'width 0.4s ease',
                                      }}
                                    />
                                  </div>
                                  <span style={{ fontWeight: 800, color: TEXT, minWidth: 40 }}>{Number.isFinite(d.usedPercent) ? `${d.usedPercent}%` : '—'}</span>
                                </div>
                              </td>
                              <td style={{ padding: '12px 14px', color: '#e2e8f0' }}>{Number.isFinite(d.freeGb) ? `${d.freeGb} ГБ` : '—'}</td>
                              <td style={{ padding: '12px 14px', color: '#e2e8f0' }}>{Number.isFinite(d.totalGb) ? `${d.totalGb} ГБ` : '—'}</td>
                              <td style={{ padding: '12px 14px', color: MUTED }}>{Number.isFinite(d.threshold) ? `${d.threshold}%` : '—'}</td>
                              <td style={{ padding: '12px 14px' }}>
                                {d.letter ? (
                                  <button
                                    type="button"
                                    disabled={loading}
                                    onClick={async (ev) => {
                                      ev.preventDefault();
                                      ev.stopPropagation();
                                      if (snapshot?.dataSource === 'remote_agent') {
                                        const ok = await requestRemoteFolderScan(d.letter);
                                        if (ok) await loadSnapshot();
                                      } else {
                                        loadSnapshot({ largestFolders: d.letter });
                                      }
                                    }}
                                    style={{
                                      padding: '8px 12px',
                                      borderRadius: 10,
                                      border: `1px solid rgba(34,211,238,0.35)`,
                                      background: ACCENT_SOFT,
                                      color: ACCENT,
                                      cursor: loading ? 'wait' : 'pointer',
                                      fontSize: 12,
                                      fontWeight: 700,
                                    }}
                                  >
                                    Топ папок
                                  </button>
                                ) : null}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p style={{ margin: '12px 0 0', fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
                    «Топ папок» — тяжёлое сканирование (как в Telegram-боте), только по кнопке.
                  </p>
                </GlassPanel>
              )}

              {logman && (
                <GlassPanel
                  style={{
                    marginBottom: 20,
                    borderColor:
                      logman.status === 'Running'
                        ? 'rgba(52, 211, 153, 0.35)'
                        : logman.status === 'Stopped'
                          ? 'rgba(251, 191, 36, 0.4)'
                          : GLASS_BORDER,
                  }}
                >
                  <SectionTitle icon={FiPieChart} hint="Сессия performance log (logman query). Не влияет на работу 1С напрямую, но совпадает с отчётами бота.">
                    Счётчики (logman)
                  </SectionTitle>
                  <div style={{ fontSize: 15, color: '#e2e8f0' }}>
                    <strong style={{ color: TEXT }}>{logman.sessionName || 'SQLServerCounters'}</strong>
                    <span style={{ margin: '0 12px', color: GLASS_BORDER }}>|</span>
                    <span
                      style={{
                        fontWeight: 800,
                        color: logman.status === 'Running' ? '#6ee7b7' : logman.status === 'Stopped' ? '#fcd34d' : MUTED,
                      }}
                    >
                      {logman.status}
                    </span>
                  </div>
                </GlassPanel>
              )}

              {((largestFoldersDrive && largestFolders.length > 0) || topFoldersCache?.items?.length) && (
                <GlassPanel style={{ marginBottom: 20, borderColor: 'rgba(129, 140, 248, 0.35)' }}>
                  <SectionTitle icon={FiList} hint={!largestFolders.length && topFoldersCache?.items?.length ? 'Кэш последнего скана; автообновление метрик список не пересчитывает.' : null}>
                    Крупные папки — {largestFoldersDrive || topFoldersCache?.drive || '?'}
                  </SectionTitle>
                  {(largestFolders.length > 0 ? largestFolders : topFoldersCache?.items || []).length > 0 ? (
                    <ol style={{ margin: 0, paddingLeft: 22, color: '#e2e8f0', fontSize: 14, lineHeight: 1.75 }}>
                      {(largestFolders.length > 0 ? largestFolders : topFoldersCache.items).map((row, i) => (
                        <li key={`${row.name}-${i}`}>
                          <span style={{ fontWeight: 600 }}>{row.name}</span>{' '}
                          <span style={{ color: MUTED }}>— {Number.isFinite(row.sizeGb) ? row.sizeGb : '—'} ГБ</span>
                        </li>
                      ))}
                    </ol>
                  ) : (
                    <p style={{ margin: 0, color: MUTED }}>Нет данных.</p>
                  )}
                </GlassPanel>
              )}
            </>
          )}

          {tab === 'journal' && (
            <GlassPanel>
              <SectionTitle icon={FiZap} hint="Онлайн — события в реальном времени, пока открыта модалка. Ниже — объединённая лента с записями из памяти сервера.">
                Журнал событий
              </SectionTitle>
              {journalRows.length === 0 ? (
                <p style={{ color: MUTED, fontSize: 14 }}>Пока нет записей. Откройте мониторинг во время смены служб или нажмите «Обновить».</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {journalRows.map((row) => {
                    const warn =
                      row.severity === 'warn' ||
                      /останов|down|notfound|disk_over|порог/i.test(String(row.kind || ''));
                    return (
                      <div
                        key={row.id}
                        style={{
                          padding: '14px 16px',
                          borderRadius: 12,
                          border: `1px solid ${warn ? 'rgba(251, 191, 36, 0.35)' : GLASS_BORDER}`,
                          background: warn ? 'rgba(120, 53, 15, 0.12)' : 'rgba(2, 6, 23, 0.35)',
                        }}
                      >
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.06em', color: ACCENT }}>
                            {eventKindLabel(row.kind)}
                          </span>
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: 999,
                              background: row.source === 'socket' ? 'rgba(34,211,238,0.2)' : 'rgba(148,163,184,0.2)',
                              color: row.source === 'socket' ? '#67e8f9' : MUTED,
                            }}
                          >
                            {row.sourceLabel}
                          </span>
                          <span style={{ fontSize: 12, color: MUTED, marginLeft: 'auto' }}>{fmtTime(row.at)}</span>
                        </div>
                        <div style={{ fontSize: 14, color: '#e2e8f0', lineHeight: 1.5 }}>{row.message}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </GlassPanel>
          )}

          {tab === 'help' && (
            <GlassPanel>
              <SectionTitle icon={FiBookOpen}>Справка</SectionTitle>
              <ul style={{ margin: 0, paddingLeft: 20, color: '#cbd5e1', fontSize: 14, lineHeight: 1.75 }}>
                <li>
                  <strong style={{ color: TEXT }}>Источник данных:</strong> обычно удалённый агент на сервере 1С (POST ingest). Если Node на том же Windows, что и 1С — локальный{' '}
                  <code style={{ color: ACCENT }}>onec-monitor-collect.ps1</code> (с кэшем ~60 с, чтобы не грузить сервер при опросе модалки).
                </li>
                <li>
                  <strong style={{ color: TEXT }}>Перезагрузка и готовность:</strong> пока Windows перезагружается, Node не работает — сигнал в Live невозможен. После старта узла в журнале появится «Перезагрузка Windows», затем при запуске SQL и 1С — «Можно подключаться к 1С» (и зелёная полоса в шапке модалки). Пока горит жёлтая полоса «ждём службы после ребута», опрос API ~6 с; в обычном режиме — ~8 с. Частота смены цифр CPU/памяти/I/O ограничена интервалом задачи агента на сервере 1С (поставьте 1 мин или чаще для почти «живого» графика).
                </li>
                <li>Во время перезагрузки API не отвечает — это ожидаемо.</li>
                <li>После плановой перезагрузки (~10:01) смотрите время загрузки Windows и таблицу служб.</li>
                <li>
                  Вкладка <strong style={{ color: TEXT }}>Журнал</strong> — live-события для администратора, пока окно открыто. <strong style={{ color: TEXT }}>Telegram:</strong> админка → <strong style={{ color: TEXT }}>Боты Telegram</strong> → блок{' '}
                  <strong style={{ color: TEXT }}>«Уведомление: сервер 1С снова в сети»</strong> — включите, укажите бота, chat_id и текст; когда монитор снова увидит готовность (SQL+1С в норме после сбоя или после долгой тишины агента при уже «ок»), сервер отправит это сообщение в группу.
                </li>
                <li>
                  <strong style={{ color: TEXT }}>Диски:</strong> логические тома (DriveType 3). Порог для{' '}
                  <strong style={{ color: TEXT }}>C:</strong> задаётся переменной <code style={{ color: ACCENT }}>ONEC_DISK_THRESHOLD_C</code> (по умолчанию 80%), для остальных —{' '}
                  <code style={{ color: ACCENT }}>ONEC_DISK_THRESHOLD_OTHER</code> (88%). На обзоре — панель-предупреждение, в журнале — события при пересечении порога.
                </li>
                <li>
                  <strong style={{ color: TEXT }}>Перезагрузка и оповещение сотрудников:</strong> «Сигнал при готовности» в шапке модалки — звук/уведомление браузера у админа. Отдельно: <strong style={{ color: TEXT }}>чат мессенджера</strong> — шаблон в группу «Общий чат» (или <code style={{ color: ACCENT }}>ONEC_READY_ANNOUNCE_CHAT_NAME</code> в .env; выкл.: <code style={{ color: ACCENT }}>ONEC_READY_ANNOUNCE_CHAT_ENABLED=0</code>). <strong style={{ color: TEXT }}>Telegram-группа</strong> — только через блок «Уведомление: сервер 1С снова в сети» в настройках ботов. Пока API недоступен, последний снимок на экране сохраняется — нормально при ребуте узла с мессенджером.
                </li>
                <li>
                  <strong style={{ color: TEXT }}>«Топ папок»:</strong> если Node собирает метрики локально через <code style={{ color: ACCENT }}>onec-monitor-collect.ps1</code>, скан запускается сразу. Если данные идут с <strong>другого</strong> ПК (агент POST), кнопка ставит том в очередь на сервере мессенджера; при следующем запуске{' '}
                  <code style={{ color: ACCENT }}>onec-monitor-agent.ps1</code> на 1С скрипт запрашивает очередь и передаёт её в collect. Обновите агент на 1С из репозитория.
                </li>
                <li>
                  <strong style={{ color: TEXT }}>Удалённый сервер 1С:</strong> на проде в .env — <code style={{ color: ACCENT }}>ONEC_MONITOR_INGEST_SECRET</code> (≥16 символов). Скрипты в{' '}
                  <code style={{ color: ACCENT }}>C:\Ksendzik\</code>: <code style={{ color: ACCENT }}>onec-monitor-collect.ps1</code>, <code style={{ color: ACCENT }}>onec-monitor-agent.ps1</code>.{' '}
                  <strong style={{ color: TEXT }}>Переменные на сервере 1С (один раз):</strong> Win+R → <code style={{ color: ACCENT }}>sysdm.cpl</code> → вкладка «Дополнительно» → «Переменные среды…» — создайте{' '}
                  <code style={{ color: ACCENT }}>ONEC_MONITOR_INGEST_URL</code> (URL …/api/admin/onec-monitor-ingest), <code style={{ color: ACCENT }}>ONEC_MONITOR_INGEST_KEY</code> (как секрет на проде),{' '}
                  <code style={{ color: ACCENT }}>ONEC_MONITOR_HOST_LABEL</code> (подпись в шапке). Удобнее «Пользователя», если задача планировщика запускается под тем же пользователем; для SYSTEM — в «Системные». После смены переменных может понадобиться перезапуск сеанса или задачи. Планировщик:{' '}
                  <code style={{ color: ACCENT }}>powershell.exe</code> → аргументы{' '}
                  <code style={{ color: ACCENT }}>-NoProfile -ExecutionPolicy Bypass -File &quot;C:\Ksendzik\onec-monitor-agent.ps1&quot;</code>. Свежесть снимка на проде:{' '}
                  <code style={{ color: ACCENT }}>ONEC_MONITOR_INGEST_MAX_AGE_MS</code> (по умолчанию 180000).
                </li>
              </ul>
              {snapshot?.note ? (
                <div style={{ marginTop: 18, paddingTop: 18, borderTop: `1px solid ${GLASS_BORDER}`, fontSize: 13, color: MUTED, lineHeight: 1.65 }}>
                  {snapshot.note}
                </div>
              ) : null}
            </GlassPanel>
          )}
        </div>
      </div>
    </div>
  );
}
